import { useState, type CSSProperties, type FC } from 'react';
import type { EditorCalendarEvent } from '../types';
import { CalendarIcon, CloseIcon, PlusIcon } from './EditorIcons';

interface EditorCalendarOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  events: EditorCalendarEvent[];
  onCreateEvent: (payload: Partial<EditorCalendarEvent>) => Promise<EditorCalendarEvent>;
  onDeleteEvent: (id: string) => Promise<void>;
}

function eventTypeLabel(type: string): string {
  switch (type) {
    case 'DEADLINE':
      return 'Дедлайн';
    case 'REVIEW':
      return 'Рецензирование';
    case 'EVENT':
      return 'Семинар / мероприятие';
    case 'MEETING':
      return 'Встреча соавторов';
    default:
      return type;
  }
}

/** Calendar — cab-overlay chrome + cabinet-* body. */
export const EditorCalendarOverlay: FC<EditorCalendarOverlayProps> = ({
  isOpen,
  onClose,
  events,
  onCreateEvent,
  onDeleteEvent,
}) => {
  const [showForm, setShowForm] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [date, setDate] = useState<string>('2026-08-25');
  const [eventType, setEventType] = useState<string>('DEADLINE');
  const [isUrgent, setIsUrgent] = useState<boolean>(false);
  const [details, setDetails] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  if (!isOpen) return null;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    try {
      setIsSubmitting(true);
      await onCreateEvent({
        title,
        event_date: date,
        event_type: eventType,
        is_urgent: isUrgent,
        details,
      });
      setTitle('');
      setDetails('');
      setShowForm(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div className="cabinet-actions" style={{ alignItems: 'center', gap: 10 }}>
              <span
                className="cabinet-badge"
                style={{ '--badge': '#e2ad3a', padding: '8px 10px' } as CSSProperties}
              >
                <CalendarIcon className="w-4 h-4" />
              </span>
              <div>
                <small>Календарь операций</small>
                <h2>Служебный календарь и дедлайны</h2>
                <p>Сроки рукописей, публикаций, заседаний редколлегии и конференций</p>
              </div>
            </div>
          </div>
          <div className="cab-top-actions">
            <button
              type="button"
              className="cab-btn primary"
              onClick={() => setShowForm(!showForm)}
            >
              <PlusIcon className="w-4 h-4" />
              Добавить событие
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              <CloseIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="cab-body">
          {showForm ? (
            <form
              onSubmit={handleCreate}
              className="cabinet-card"
              style={{ '--accent': '#e2ad3a', marginBottom: 14 } as CSSProperties}
            >
              <h4 style={{ margin: '0 0 12px', fontSize: 13 }}>Новое календарное событие</h4>
              <div className="cabinet-grid2">
                <label className="cabinet-field">
                  <span>Название события</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Срок подачи материалов"
                  />
                </label>
                <label className="cabinet-field">
                  <span>Дата события</span>
                  <input
                    className="cabinet-input"
                    type="date"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                  />
                </label>
                <label className="cabinet-field">
                  <span>Тип</span>
                  <select
                    className="cabinet-select"
                    value={eventType}
                    onChange={(e) => setEventType(e.target.value)}
                  >
                    <option value="DEADLINE">Дедлайн</option>
                    <option value="REVIEW">Рецензирование</option>
                    <option value="EVENT">Семинар / мероприятие</option>
                    <option value="MEETING">Встреча соавторов</option>
                  </select>
                </label>
                <label className="cabinet-field" style={{ justifyContent: 'flex-end' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 18 }}>
                    <input
                      type="checkbox"
                      id="urgentCheck"
                      checked={isUrgent}
                      onChange={(e) => setIsUrgent(e.target.checked)}
                    />
                    Срочный статус
                  </span>
                </label>
              </div>
              <div className="cabinet-actions" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="cabinet-btn" onClick={() => setShowForm(false)}>
                  Отмена
                </button>
                <button type="submit" className="cabinet-btn primary" disabled={isSubmitting}>
                  Сохранить
                </button>
              </div>
            </form>
          ) : null}

          <div className="cabinet-stack">
            {events.map((ev) => (
              <article
                key={ev.id}
                className="cabinet-card"
                style={
                  {
                    '--accent': ev.is_urgent ? '#ff7b65' : '#e2ad3a',
                  } as CSSProperties
                }
              >
                <div className="cabinet-actions">
                  <span style={{ fontSize: 12, color: '#e2ad3a', fontWeight: 700 }}>
                    {ev.event_date}
                  </span>
                  {ev.is_urgent ? (
                    <span className="cabinet-badge" style={{ '--badge': '#ff7b65' } as CSSProperties}>
                      Срочно
                    </span>
                  ) : null}
                  <span className="cabinet-badge" style={{ '--badge': '#8faebb' } as CSSProperties}>
                    {eventTypeLabel(ev.event_type)}
                  </span>
                  <span className="cabinet-spacer" />
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => void onDeleteEvent(ev.id)}
                    aria-label="Удалить событие"
                  >
                    <CloseIcon className="w-4 h-4" />
                  </button>
                </div>
                <strong style={{ display: 'block', marginTop: 8 }}>{ev.title}</strong>
                {ev.details ? (
                  <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
                    {ev.details}
                  </p>
                ) : null}
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
